import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, Plus, Trash2, AlertTriangle, History, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const STATUSES = ["Proposed", "In review", "Decided", "Deferred", "Rejected"];
const OPEN_STATUSES = ["Proposed", "In review"];
const statusCls = (st) => st === "Decided" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
  : st === "Rejected" ? "text-red-700 bg-red-50 border-red-200"
  : st === "Deferred" ? "text-zinc-500 bg-zinc-50 border-zinc-200"
  : "text-amber-700 bg-amber-50 border-amber-200";
const isOverdue = (i) => i.due_by && OPEN_STATUSES.includes(i.status) && new Date(i.due_by) < new Date(new Date().toDateString());
const inputCls = "border border-zinc-300 rounded px-2 py-1 text-[12px] bg-white";

function DecisionRow({ item, onSave, onDelete, busy }) {
  const [expand, setExpand] = useState(false);
  const [decision, setDecision] = useState(item.decision || "");
  const patch = (body) => onSave(item.id, body);
  const overdue = isOverdue(item);
  return (
    <div data-testid={`decision-row-${item.id}`} className={`px-3 py-2.5 ${overdue ? "bg-red-50/40" : ""}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <span className="text-[11px] text-zinc-400 font-mono w-24 pt-1 flex-none">{item.ref}</span>
        <div className="flex-1 min-w-[240px]">
          <button onClick={() => setExpand((v) => !v)} className="text-left" data-testid={`decision-title-${item.id}`}>
            <div className="text-[13px] text-zinc-800 font-medium">{item.title}</div>
          </button>
          <div className="text-[11px] text-zinc-400 mt-0.5">
            Owner: {item.owner}{item.linked_move ? ` · Unblocks: ${item.linked_move}` : ""}
            {item.status === "Decided" && item.decided_on && (
              <span className="text-emerald-600"> · Decided {new Date(item.decided_on).toLocaleDateString("en-GB")}{item.decided_by ? ` by ${item.decided_by}` : ""}</span>
            )}
          </div>
          {overdue && <span className="inline-flex items-center gap-1 text-[10px] text-red-600 font-semibold mt-1"><AlertTriangle size={11} /> Decide-by passed</span>}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <select data-testid={`decision-status-${item.id}`} className={`${inputCls} font-semibold ${statusCls(item.status)}`} value={item.status} disabled={busy}
            onChange={(e) => patch({ status: e.target.value })}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input data-testid={`decision-owner-${item.id}`} className={`${inputCls} w-28`} defaultValue={item.owner} disabled={busy}
            onBlur={(e) => e.target.value !== item.owner && patch({ owner: e.target.value })} title="Accountable owner" />
          <input data-testid={`decision-due-${item.id}`} type="date" className={inputCls} defaultValue={item.due_by || ""} disabled={busy}
            onChange={(e) => patch({ due_by: e.target.value || null })} title="Decide-by date" />
          {(item.updates || []).length > 0 && (
            <button data-testid={`decision-hist-btn-${item.id}`} onClick={() => setExpand((v) => !v)} title="History"
              className="text-zinc-400 hover:text-black p-1"><History size={14} /></button>
          )}
          <button data-testid={`decision-delete-${item.id}`} onClick={() => onDelete(item)} disabled={busy}
            className="text-zinc-300 hover:text-red-600 p-1" title={item.seed_ref ? "Hide (seeded)" : "Delete"}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      {expand && (
        <div data-testid={`decision-detail-${item.id}`} className="mt-2 ml-24 space-y-2 border-l-2 border-zinc-200 pl-3">
          {item.context && <p className="text-[12px] text-zinc-600">{item.context}</p>}
          {(item.options || []).length > 0 && (
            <div className="text-[12px] text-zinc-600">
              <span className="text-[10px] tracking-[0.12em] uppercase text-zinc-400">Options</span>
              <ul className="list-disc pl-4 mt-0.5">{item.options.map((o, i) => <li key={i}>{o}</li>)}</ul>
            </div>
          )}
          {item.recommendation && <p className="text-[12px] text-zinc-700"><span className="text-[10px] tracking-[0.12em] uppercase text-zinc-400">Recommendation</span><br />{item.recommendation}</p>}
          <div className="flex items-end gap-2 flex-wrap">
            <label className="text-[11px] text-zinc-500 flex-1 min-w-[220px]">
              <span className="block mb-0.5">Decision made (record the call)</span>
              <input data-testid={`decision-record-${item.id}`} className={`${inputCls} w-full`} value={decision}
                onChange={(e) => setDecision(e.target.value)} placeholder="e.g. Approved with tranche funding" />
            </label>
            <button data-testid={`decision-record-save-${item.id}`} disabled={busy || !decision.trim()}
              onClick={() => patch({ decision, status: "Decided" })}
              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase bg-emerald-600 text-white px-3 py-1.5 rounded disabled:opacity-40">
              <CheckCircle2 size={13} /> Mark decided
            </button>
          </div>
          {(item.updates || []).length > 0 && (
            <div className="space-y-1 pt-1">
              {item.updates.map((u, i) => (
                <div key={i} className="text-[11px] text-zinc-500">
                  <span className="text-zinc-400">{new Date(u.at).toLocaleDateString("en-GB")}</span>
                  {u.from_status && <> · {u.from_status} → <b>{u.status}</b></>}
                  {u.note && <> · {u.note}</>}
                  {u.by && <span className="text-zinc-300"> · {u.by}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AddDecisionForm({ onAdd, busy }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", context: "", options: "", recommendation: "", owner: "", linked_move: "", due_by: "" });
  const submit = async () => {
    if (!f.title.trim()) { toast.error("Enter a decision title"); return; }
    await onAdd(f);
    setF({ title: "", context: "", options: "", recommendation: "", owner: "", linked_move: "", due_by: "" });
    setOpen(false);
  };
  if (!open) return (
    <button data-testid="decision-add-toggle" onClick={() => setOpen(true)}
      className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.12em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-3 py-1.5 rounded hover:bg-[var(--e360-primary)]/5">
      <Plus size={14} /> Add decision
    </button>
  );
  return (
    <div data-testid="decision-add-form" className="border border-zinc-200 rounded-lg p-3 bg-zinc-50 space-y-2">
      <input data-testid="decision-add-title" className={`${inputCls} w-full`} placeholder="Decision required (e.g. Approve target operating model)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      <textarea data-testid="decision-add-context" rows={2} className={`${inputCls} w-full resize-y`} placeholder="Context / why it matters" value={f.context} onChange={(e) => setF({ ...f, context: e.target.value })} />
      <textarea data-testid="decision-add-options" rows={2} className={`${inputCls} w-full resize-y`} placeholder="Options — one per line" value={f.options} onChange={(e) => setF({ ...f, options: e.target.value })} />
      <input data-testid="decision-add-reco" className={`${inputCls} w-full`} placeholder="Recommendation" value={f.recommendation} onChange={(e) => setF({ ...f, recommendation: e.target.value })} />
      <div className="grid sm:grid-cols-3 gap-2">
        <input data-testid="decision-add-owner" className={inputCls} placeholder="Accountable owner" value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} />
        <input data-testid="decision-add-move" className={inputCls} placeholder="Unblocks (move/stream)" value={f.linked_move} onChange={(e) => setF({ ...f, linked_move: e.target.value })} />
        <input data-testid="decision-add-due" type="date" className={inputCls} value={f.due_by} onChange={(e) => setF({ ...f, due_by: e.target.value })} title="Decide-by date" />
      </div>
      <div className="flex gap-2">
        <button data-testid="decision-add-submit" onClick={submit} disabled={busy}
          className="text-[12px] tracking-[0.12em] uppercase bg-[var(--e360-primary)] text-white px-4 py-1.5 rounded disabled:opacity-40">Add</button>
        <button onClick={() => setOpen(false)} className="text-[12px] tracking-[0.12em] uppercase text-zinc-500 px-3 py-1.5">Cancel</button>
      </div>
    </div>
  );
}

const Stat = ({ label, value, cls = "text-black" }) => (
  <div className="text-center">
    <div className={`text-2xl font-semibold ${cls}`}>{value}</div>
    <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400">{label}</div>
  </div>
);

const FilterChip = ({ active, onClick, children, testId }) => (
  <button data-testid={testId} onClick={onClick}
    className={`text-[11px] tracking-[0.1em] uppercase px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-[var(--e360-primary)] text-white border-[var(--e360-primary)]" : "text-zinc-500 border-zinc-300 hover:border-zinc-400"}`}>
    {children}
  </button>
);

export default function DecisionLog({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("outstanding"); // outstanding | decided | all

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/decisions`);
      setData(data);
    } catch { toast.error("Could not load the decision log"); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const withBusy = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); setData(data); if (ok) toast.success(ok); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  const sync = () => withBusy(() => api.post(`/admin/enterprise360/clients/${clientId}/decisions/seed`), "Synced from blueprint");
  const add = (body) => withBusy(() => api.post(`/admin/enterprise360/clients/${clientId}/decisions`, body), "Decision added");
  const save = (itemId, body) => withBusy(() => api.put(`/admin/enterprise360/clients/${clientId}/decisions/${itemId}`, body));
  const del = (item) => {
    if (!window.confirm(item.seed_ref ? "Hide this seeded decision?" : "Delete this decision?")) return;
    withBusy(() => api.delete(`/admin/enterprise360/clients/${clientId}/decisions/${item.id}`), "Removed");
  };

  const s = data?.summary || {};
  const allItems = data?.items || [];
  const items = useMemo(() => allItems.filter((i) =>
    filter === "all" ? true
      : filter === "decided" ? i.status === "Decided"
      : OPEN_STATUSES.includes(i.status)
  ), [allItems, filter]);

  return (
    <div data-testid="decision-log">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <p className="text-[12px] text-zinc-500 max-w-xl">Decisions sought → decisions made, each with an accountable owner, a decide-by date and a link to the move it unblocks.</p>
        <div className="flex items-center gap-2">
          {data?.seed_available > 0 && (
            <span data-testid="decision-seed-available" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
              {data.seed_available} new from blueprint
            </span>
          )}
          <button data-testid="decision-sync-btn" onClick={sync} disabled={busy}
            className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.12em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-3 py-1.5 rounded hover:bg-[var(--e360-primary)]/5 disabled:opacity-40">
            <RefreshCw size={13} /> Sync from blueprint
          </button>
        </div>
      </div>

      <div data-testid="decision-summary" className="flex items-center gap-6 flex-wrap border border-zinc-200 rounded-lg px-4 py-3 mb-4">
        <Stat label="Outstanding" value={s.outstanding ?? 0} cls={s.outstanding ? "text-amber-600" : "text-black"} />
        <Stat label="Overdue" value={s.overdue ?? 0} cls={s.overdue ? "text-red-600" : "text-black"} />
        <Stat label="Decided" value={s.decided ?? 0} cls="text-emerald-600" />
        <Stat label="Total" value={s.total ?? 0} cls="text-zinc-400" />
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="decision-filters">
        <FilterChip testId="decision-filter-outstanding" active={filter === "outstanding"} onClick={() => setFilter("outstanding")}>Outstanding ({s.outstanding ?? 0})</FilterChip>
        <FilterChip testId="decision-filter-decided" active={filter === "decided"} onClick={() => setFilter("decided")}>Decided ({s.decided ?? 0})</FilterChip>
        <FilterChip testId="decision-filter-all" active={filter === "all"} onClick={() => setFilter("all")}>All ({allItems.length})</FilterChip>
      </div>

      <div className="mb-3"><AddDecisionForm onAdd={add} busy={busy} /></div>

      {loading ? (
        <div className="text-sm text-zinc-400 py-6 text-center">Loading decision log…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-zinc-500 py-6 text-center">{allItems.length === 0 ? "No decisions yet. Sync from the blueprint or add one manually." : "No decisions match the current filter."}</div>
      ) : (
        <div className="border border-zinc-200 rounded-lg divide-y" data-testid="decision-list">
          {items.map((it) => <DecisionRow key={it.id} item={it} onSave={save} onDelete={del} busy={busy} />)}
        </div>
      )}
    </div>
  );
}
